/* eslint-disable react/jsx-key */
import {
  deleteFetcher,
  getFetcher,
  putFetcher,
  patchFetcher,
} from '@/api/fetchers';
import { createKey } from '@/api/params';
import { BooleanBadge } from '@/components/Badge/BooleanBadge';
import { HourMinuteInput } from '@/components/Input/HourMinuteInput';
import { PriceInput } from '@/components/Input/PriceInput';
import { ClampedTextCell } from '@/components/List/Cell/ClampedTextCell';
import { TextHeader } from '@/components/List/Header/TextHeader';
import { List } from '@/components/List/List';
import { dateFormat, dateFormatUI } from '@/index';
import { type RecordsRequest } from '@/schemas/API/GET/billing/record/request';
import { type Record as RecordType } from '@/schemas/API/GET/billing/record/response';
import { type Billing } from '@/schemas/API/GET/billing/response';
import { type Report } from '@/schemas/API/GET/report/response';
import {
  Button,
  Group,
  HoverCard,
  Modal,
  MultiSelect,
  Stack,
  Textarea,
  rem,
  CheckIcon,
} from '@mantine/core';
import { FiFileText } from 'react-icons/fi';
import { DateInput } from '@mantine/dates';
import dayjs from 'dayjs';
import {
  type ReactElement,
  useState,
  useEffect,
  useRef,
  useContext,
} from 'react';
import { AlignedCell } from '@/components/List/Cell/AlignedCell';
import { IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { FaRegTrashAlt } from 'react-icons/fa';
import { convertToPutRecord } from '@/schemas/converters/billingRecordsConverter';
import { displayError } from '@/utils/error/errorHandler';
import {
  type PlanningContextType,
  StateContext,
} from '@/layouts/Planning/PlanningLayout';
import PlanningActionHandler from '@/pages/Planning/Helpers/PlanningActionHandler';
import { badNotification } from '@/components/Notification/Notification';

export type ModalType = 'report' | 'billing';

export interface ModalInfo {
  id: number;
  type: ModalType;
  title: string;
}

export interface ModalProps {
  opened: boolean;
  onClose: () => void;
  spec?: ModalInfo;
}

const same = (
  original: RecordType | undefined,
  modified: RecordType
): boolean => {
  if (original === undefined) return false;
  const { manual, totalPrice, ...originalVal } = original;
  const { manual: _, totalPrice: __, ...modifiedVal } = modified;
  return JSON.stringify(originalVal) === JSON.stringify(modifiedVal);
};

const AuditDateCell = (
  handleFieldChange: (
    recordId: number,
    fieldName: keyof RecordType,
    newValue: RecordType[keyof RecordType]
  ) => void
) => {
  const Cell = (record: RecordType) => (
    <DateInput
      valueFormat={dateFormatUI}
      value={new Date(record.date)}
      variant="filled"
      onChange={input => {
        handleFieldChange(record.id, 'date', dayjs(input).format(dateFormat));
      }}
    />
  );
  return Cell;
};

const AuditMultiSelectCell = (spec: ModalInfo | undefined) => {
  const Cell = (record: RecordType) => (
    <MultiSelect
      value={
        spec?.type === 'billing'
          ? record.emps?.map(emp => emp.firstName + ' ' + emp.lastName)
          : [record.client?.firstName + ' ' + record.client?.lastName]
      }
      variant="filled"
    />
  );
  return Cell;
};

const AuditHourInputCell = (
  handleFieldChange: (
    recordId: number,
    fieldName: keyof RecordType,
    newValue: RecordType[keyof RecordType]
  ) => void
) => {
  const Cell = (record: RecordType) => (
    <HourMinuteInput
      variant="filled"
      value={record.duration}
      onChange={input => {
        handleFieldChange(record.id, 'duration', input);
        handleFieldChange(
          record.id,
          'totalPrice',
          Math.round((record.hourRate * input) / 60)
        );
      }}
    />
  );
  return Cell;
};

const AuditPriceInputCell = (
  handleFieldChange: (
    recordId: number,
    fieldName: keyof RecordType,
    newValue: RecordType[keyof RecordType]
  ) => void
) => {
  const Cell = (record: RecordType) => (
    <PriceInput
      variant="filled"
      value={record.hourRate}
      onChange={input => {
        handleFieldChange(record.id, 'hourRate', input);
        handleFieldChange(
          record.id,
          'totalPrice',
          Math.round((input * record.duration) / 60)
        );
      }}
      currency={'Kč'}
    />
  );
  return Cell;
};

const AuditTotalPriceCell = (record: RecordType) => (
  <ClampedTextCell>{String(record.totalPrice)} Kč</ClampedTextCell>
);

const AuditAssignmentCell = () => <ClampedTextCell>Úkol</ClampedTextCell>;

const AuditCompletedCell = (
  handleFieldChange: (
    recordId: number,
    fieldName: keyof RecordType,
    newValue: RecordType[keyof RecordType]
  ) => void
) => {
  const Cell = (record: RecordType) => (
    <AlignedCell align="center">
      <BooleanBadge
        clickable
        onClick={() => {
          handleFieldChange(record.id, 'completed', !record.completed);
        }}
        successLabel={'Ano'}
        failLabel={'Ne'}
        successCond={() => record.completed}
      />
    </AlignedCell>
  );
  return Cell;
};

const AuditNoteCell = (
  handleFieldChange: (
    recordId: number,
    fieldName: keyof RecordType,
    newValue: RecordType[keyof RecordType]
  ) => void
) => {
  const Cell = (record: RecordType) => (
    <Group>
      <HoverCard width={280} shadow="md">
        <HoverCard.Target>
          <div className="flex w-full justify-center">
            <FiFileText />
          </div>
        </HoverCard.Target>
        <HoverCard.Dropdown className="p-0 border-0">
          <Textarea
            value={record.note}
            maxRows={4}
            autosize
            onChange={input =>
              handleFieldChange(record.id, 'note', input.currentTarget.value)
            }
          />
        </HoverCard.Dropdown>
      </HoverCard>
    </Group>
  );
  return Cell;
};

const AuditCheckedCell = (
  handleFieldChange: (
    recordId: number,
    fieldName: keyof RecordType,
    newValue: RecordType[keyof RecordType]
  ) => void
) => {
  const Cell = (record: RecordType) => (
    <AlignedCell align="center">
      <BooleanBadge
        clickable
        onClick={() => {
          handleFieldChange(record.id, 'checked', !record.checked);
        }}
        successLabel={'Ano'}
        failLabel={'Ne'}
        successCond={() => record.checked}
      />
    </AlignedCell>
  );
  return Cell;
};

const AuditDeleteCell = (handleDelete: (id: number) => Promise<void>) => {
  const Cell = (record: RecordType) => (
    <AlignedCell align="center">
      <FaRegTrashAlt
        className="hover:text-red-600 cursor-pointer"
        onClick={async () => handleDelete(record.id)}
      />
    </AlignedCell>
  );
  return Cell;
};

export const AuditModal = (props: ModalProps): ReactElement => {
  const { opened, onClose, spec } = props;
  const [originalRecords, setOriginalRecords] = useState<RecordType[]>([]);
  const [modifiedRecords, setModifiedRecords] = useState<
    Map<number, RecordType>
  >(new Map());
  const [maxVirtualPage, setMaxVirtualPage] = useState<number>(2);
  const [requestParams, setRequestParams] = useState<RecordsRequest>({
    page: 1,
    sort: [],
    size: 10,
  });
  const [statement, setStatement] = useState<Billing | Report | null>(null);
  const { planId } = useContext<PlanningContextType>(StateContext);

  const { dispatchBillingOrReportGivenIds, exportBillingOrReportGivenIds } =
    PlanningActionHandler();

  const handleClose = () => {
    if (modifiedRecords.size > 0) {
      notifications.show({
        title: 'Neuložené změny',
        message: 'Uložte nebo zahoďte změny a zkuste znovu',
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
    } else {
      setOriginalRecords([]);
      setModifiedRecords(new Map());
      setRequestParams({
        page: 1,
        sort: [],
        size: 9,
      });
      onClose();
    }
  };

  const headerRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>(300);

  useEffect(() => {
    if (opened) {
      let padding = 0;
      let total = 500;
      let header = 40;
      if (modalRef.current) {
        total = modalRef.current.clientHeight;
      }
      if (headerRef.current) {
        header = headerRef.current.clientHeight;
      }
      if (bodyRef.current) {
        const computedStyles = window.getComputedStyle(bodyRef.current);
        padding = parseInt(computedStyles.paddingBottom, 10);
      }
      setMaxHeight(total - header - padding);
    }
  }, [opened]);

  const setRequestParam = <K extends keyof RecordsRequest>(
    fieldName: K,
    value: RecordsRequest[K] | null
  ): void => {
    setRequestParams(prevParams => {
      return { ...prevParams, [fieldName]: value };
    });
  };

  const fetchData = async () => {
    getFetcher(`/${spec?.type}s/${spec?.id}/records` + createKey(requestParams))
      .then(res => {
        setMaxVirtualPage(res.totalPages);
        const newRecords = originalRecords.concat(res.content);
        setOriginalRecords(newRecords);
      })
      .catch(() => console.error);
  };

  const fetchBillingOrReport = async () => {
    if (spec?.type && spec?.id) {
      const url = `/${spec.type}s/${spec.id}`;
      const data = await getFetcher(url);
      setStatement(data);
    }
  };

  useEffect(() => {
    if (opened) {
      fetchBillingOrReport();
      fetchData();
    }
  }, [spec?.type, spec?.id, requestParams, opened]);

  const handleFieldChange = (
    recordId: number,
    fieldName: keyof RecordType,
    newValue: RecordType[keyof RecordType]
  ) => {
    setModifiedRecords(prevMap => {
      const newMap = new Map(prevMap);
      if (newMap.has(recordId)) {
        const record = newMap.get(recordId);
        if (record) {
          const updatedRecord = { ...record, [fieldName]: newValue };
          if (
            !same(
              originalRecords.find(rec => rec.id === recordId),
              updatedRecord
            )
          ) {
            newMap.set(recordId, updatedRecord);
          } else {
            newMap.delete(recordId);
          }
        }
      } else {
        const record = originalRecords.find(rec => rec.id === recordId);
        if (record) {
          const updatedRecord = { ...record, [fieldName]: newValue };
          if (
            !same(
              originalRecords.find(rec => rec.id === recordId),
              updatedRecord
            )
          ) {
            newMap.set(recordId, updatedRecord);
          }
        }
      }
      return newMap;
    });
  };

  const handleSave = async () => {
    const records = Array.from(modifiedRecords.values());
    const toUrl = (id: number) => `/${spec?.type}s/${spec?.id}/records/${id}`;

    const settled = await Promise.allSettled(
      records.map(async r => putFetcher(toUrl(r.id), convertToPutRecord(r)))
    );

    const updatedById = new Map<number, any>();
    let failed = 0;

    settled.forEach((res, i) => {
      const id = records[i].id;
      if (res.status === 'fulfilled') {
        updatedById.set(id, res.value);
      } else {
        failed++;
      }
    });

    if (updatedById.size === 0) {
      badNotification(
        'Špatný požadavek!',
        'Ukládání selhalo. Zkontrolujte, zda výkaz není zamknutý.'
      );
      return;
    }

    setOriginalRecords(prev => prev.map(or => updatedById.get(or.id) ?? or));

    if (failed > 0) {
      badNotification(
        'Částečně uloženo',
        `${failed} záznamů se nepodařilo uložit.`
      );
    }

    setModifiedRecords(new Map());
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteFetcher(`/${spec?.type}s/${spec?.id}/records/${id}`);
      setOriginalRecords(prevRecords =>
        prevRecords.filter(record => record.id !== id)
      );
      setModifiedRecords(prevMap => {
        const newMap = new Map(prevMap);
        newMap.delete(id);
        return newMap;
      });

      notifications.show({
        message: 'Záznam byl úspěšně smazán.',
        color: 'green',
        autoClose: 1000,
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
    } catch (err) {
      displayError(err);
    }
  };

  const handleLockToggle = async () => {
    if (spec?.type && spec?.id) {
      const url = `/${spec.type}s/${spec.id}`;
      try {
        await patchFetcher(url, {
          locked: !statement?.locked,
        });
        fetchBillingOrReport(); // Refresh the statement data
        notifications.show({
          message: `Záznam byl úspěšně ${
            statement?.locked ? 'odemčen' : 'uzamčen'
          }.`,
          color: 'green',
          autoClose: 1000,
          icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
        });
      } catch (err) {
        displayError(err);
      }
    }
  };

  return (
    <Modal.Root
      opened={opened}
      transitionProps={{ duration: 0 }}
      onClose={handleClose}
      size="calc(100vw - 4rem)"
    >
      <Modal.Overlay />
      <Modal.Content ref={modalRef} style={{ height: 'calc(100vh - 3rem)' }}>
        <Modal.Header ref={headerRef}>
          <Modal.Title className="w-full">{spec?.title}</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body ref={bodyRef}>
          <Stack gap="xs" style={{ height: `${maxHeight}px` }}>
            <List
              lazyLoad={{
                onLoad: () => {
                  setRequestParam('page', requestParams.page + 1);
                },
                disabled: requestParams.page === maxVirtualPage,
              }}
              loading={false}
              columns={[
                <TextHeader>Datum</TextHeader>,
                <TextHeader>
                  {spec?.type === 'billing' ? 'Zaměstnanci' : 'Klienti'}
                </TextHeader>,
                <TextHeader>Hodiny</TextHeader>,
                <TextHeader>Hodinová sazba</TextHeader>,
                <TextHeader>Celková sazba</TextHeader>,
                <TextHeader>Typ položky</TextHeader>,
                <TextHeader>Vykonána</TextHeader>,
                <TextHeader>Poznámka</TextHeader>,
                <TextHeader>Kontrola</TextHeader>,
                <TextHeader>Odstranit</TextHeader>,
              ]}
              content={{
                items: originalRecords
                  .map(record => modifiedRecords?.get(record.id) ?? record)
                  .filter(Boolean),
                renders: [
                  AuditDateCell(handleFieldChange),
                  AuditMultiSelectCell(spec),
                  AuditHourInputCell(handleFieldChange),
                  AuditPriceInputCell(handleFieldChange),
                  AuditTotalPriceCell,
                  AuditAssignmentCell,
                  AuditCompletedCell(handleFieldChange),
                  AuditNoteCell(handleFieldChange),
                  AuditCheckedCell(handleFieldChange),
                  AuditDeleteCell(handleDelete),
                ],
              }}
            />
            <Group gap="xs" justify="flex-end">
              <Button
                variant="outline"
                onClick={() => {
                  if (
                    planId !== undefined &&
                    spec !== undefined &&
                    statement !== null
                  ) {
                    exportBillingOrReportGivenIds(planId, spec.type, [
                      statement.id,
                    ]);
                  }
                }}
              >
                Uložit PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (
                    planId !== undefined &&
                    spec !== undefined &&
                    statement !== null
                  ) {
                    dispatchBillingOrReportGivenIds(planId, spec.type, [
                      statement.id,
                    ]);
                  }
                }}
              >
                Odeslat e-mailem
              </Button>
              <Button variant="outline" onClick={handleLockToggle}>
                {statement?.locked ? 'Odemknout' : 'Uzamknout'}
              </Button>
              <Button
                disabled={modifiedRecords.size === 0}
                variant="outline"
                onClick={() => setModifiedRecords(new Map())}
              >
                Zrušit změny
              </Button>
              <Button
                disabled={modifiedRecords.size === 0}
                onClick={handleSave}
              >
                Uložit změny
              </Button>
            </Group>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
